import { useKitbagAuth } from '@statsbomb/kitbag-auth';
import { useAtomValue, useSetAtom } from 'jotai';
import { useIntl } from 'react-intl';
import useCallbackSafeAtomValue from '../atoms/callbackSafe';
import { videoRefreshInProgressAtom } from '../atoms/polling';
import { selectedMetadataAtom } from '../atoms/selectedMetadata';
import {
  videoSearchMatchDataAtom,
  videoSearchResultsAreUpdatingAtom,
  videoSearchResultsAtom,
  videoSearchResultsErrorAtom,
  videosAwaitingApprovalAtom,
} from '../atoms/videoListings';
import { videosPendingApprovalSerialiser } from '../serialisers/videos';
import useApi, { API_ENDPOINTS } from './api';

export const VIDEO_STATUS = { PENDING: 'PENDING' };
const FIVE_SECONDS = 5000;

export default function useVideoListings() {
  const api = useApi();
  const intl = useIntl();

  const { getIdTokenClaims } = useKitbagAuth();

  const videoRefreshInProgress = useCallbackSafeAtomValue(
    videoRefreshInProgressAtom,
  );

  const setVideoRefreshInProgress = useSetAtom(videoRefreshInProgressAtom);

  const setVideosAwaitingApproval = useSetAtom(videosAwaitingApprovalAtom);
  const setVideoSearchResults = useSetAtom(videoSearchResultsAtom);
  const setVideoSearchMatchData = useSetAtom(videoSearchMatchDataAtom);
  const setVideoSearchResultsError = useSetAtom(videoSearchResultsErrorAtom);
  const setVideoSearchResultsAreUpdating = useSetAtom(
    videoSearchResultsAreUpdatingAtom,
  );

  const selectedMetadata = useAtomValue(selectedMetadataAtom);
  const { sport: selectedSport, matchId: selectedMatchId } = selectedMetadata;

  const REFRESH_SEARCH_ERROR = intl.formatMessage({
    defaultMessage: 'Failed to search for videos. Please try again',
    id: 'bb430',
  });

  const refreshVideosAwaitingApproval = async () => {
    if (!videoRefreshInProgress()) {
      setVideoRefreshInProgress(true);
      const { email } = await getIdTokenClaims();

      try {
        const response = await api.get(`${API_ENDPOINTS.videoService}/videos`, {
          params: { email, status: VIDEO_STATUS.PENDING },
        });
        setVideosAwaitingApproval(
          videosPendingApprovalSerialiser(response?.data?.listings || []),
        );
        setTimeout(() => {
          setVideoRefreshInProgress(false);
        }, FIVE_SECONDS);
      } catch {
        setVideoRefreshInProgress(false);
      }
    }
  };

  const refreshVideoSearchResults = async (sport, matchId) => {
    setVideoSearchResultsError();
    setVideoSearchResultsAreUpdating(true);
    const response = await api.get(
      // eslint-disable-next-line max-len
      `${API_ENDPOINTS.videoService}/matches/${sport || selectedSport}/${
        matchId || selectedMatchId
      }/videos?include_urls=True&include_metadata=True&include_quality_assurance_info=True&include_streams=True`,
    );
    if (response?.data) {
      setVideoSearchResults(response.data?.videos || []);
      setVideoSearchMatchData(response.data?.match_data || null);
    } else {
      setVideoSearchResults([]);
      setVideoSearchMatchData(null);
      setVideoSearchResultsError({
        text: REFRESH_SEARCH_ERROR,
        type: 'error',
      });
    }
    setVideoSearchResultsAreUpdating(false);
  };

  return {
    refreshVideosAwaitingApproval,
    refreshVideoSearchResults,
  };
}
